import {
  FaSun, FaCloudSun, FaUtensils, FaCookieBite, FaMoon,
} from 'react-icons/fa';

/** Canonical meal slots — labels match Excel (Bf, Mid Mor Snack, Lunch, Evng Snack, Dinner). */
export const MEAL_SLOT_TYPES = [
  {
    id: 'breakfast',
    label: 'Breakfast',
    excelKeys: ['Bf', 'Breakfast', 'Break Fast'],
    icon: FaSun,
    className: 'sp-slot-type--breakfast',
  },
  {
    id: 'mid_morning',
    label: 'Mid Mor Snack',
    excelKeys: ['Mid Mor Snack', 'Mid Morning', 'Mid-morning snack'],
    icon: FaCloudSun,
    className: 'sp-slot-type--mid-morning',
  },
  {
    id: 'lunch',
    label: 'Lunch',
    excelKeys: ['Lunch'],
    icon: FaUtensils,
    className: 'sp-slot-type--lunch',
  },
  {
    id: 'evening_snack',
    label: 'Evng Snack',
    excelKeys: ['Evng Snack', 'Evening snack', 'Afternoon snack'],
    icon: FaCookieBite,
    className: 'sp-slot-type--evening-snack',
  },
  {
    id: 'dinner',
    label: 'Dinner',
    excelKeys: ['Dinner'],
    icon: FaMoon,
    className: 'sp-slot-type--dinner',
  },
];

const WEEKDAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export function resolveSlotType(slotKey = '') {
  const key = String(slotKey).trim();
  return MEAL_SLOT_TYPES.find((t) => t.excelKeys.some((k) => k.toLowerCase() === key.toLowerCase()))
    || MEAL_SLOT_TYPES.find((t) => key.toLowerCase().includes(t.id.replace('_', ' ')))
    || null;
}

export function sortMealsBySlotOrder(meals = []) {
  const order = MEAL_SLOT_TYPES.map((t) => t.id);
  return [...meals].sort((a, b) => {
    const ai = order.indexOf(a.slotId || resolveSlotType(a.slot)?.id);
    const bi = order.indexOf(b.slotId || resolveSlotType(b.slot)?.id);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
}

export function sortWeekdays(weeklyMenu = []) {
  return [...weeklyMenu].sort((a, b) => {
    const ai = WEEKDAY_ORDER.indexOf(a.day);
    const bi = WEEKDAY_ORDER.indexOf(b.day);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
}

/** Normalize legacy sampleDays / weight-loss sections into weekday rows with all slot types. */
export function buildWeeklyMenu(plan) {
  if (plan?.weeklyMenu?.length) {
    return sortWeekdays(plan.weeklyMenu.map(normalizeWeekdayRow));
  }

  if (plan?.sampleDays?.length) {
    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return plan.sampleDays.map((row, index) => normalizeWeekdayRow({
      day: dayNames[index] || row.day,
      meals: row.meals,
    }));
  }

  if (plan?.sampleWeeks?.length) {
    return buildWeeklyFromWeightLossSections(plan.sampleWeeks);
  }

  return [];
}

function normalizeWeekdayRow(row) {
  const meals = (row.meals || []).map((m) => {
    const slotType = resolveSlotType(m.slot || m.slotId);
    return {
      slotId: m.slotId || slotType?.id || m.slot,
      slot: slotType?.label || m.slot,
      item: m.item || m.menu || '—',
      portion: m.portion || '',
      calories: m.calories || '',
    };
  });
  return {
    day: row.day,
    meals: sortMealsBySlotOrder(meals),
  };
}

function buildWeeklyFromWeightLossSections(sampleWeeks) {
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const byDay = {};

  for (const week of sampleWeeks) {
    for (const section of week.sections || []) {
      if (section.description) continue;
      const slotType = resolveSlotType(section.title) || resolveSlotType(section.slot);
      const slotId = slotType?.id;
      const slotLabel = slotType?.label || section.title;

      for (const day of section.days || []) {
        if (!byDay[day]) byDay[day] = [];
        byDay[day].push({
          slotId,
          slot: slotLabel,
          item: section.menus?.[day] || '—',
          portion: '',
          calories: '',
        });
      }
    }
  }

  return sortWeekdays(
    days.filter((d) => byDay[d]).map((day) => normalizeWeekdayRow({ day, meals: byDay[day] })),
  );
}

const PLACEHOLDER_BY_SLOT = {
  breakfast: 'Chef’s rotating breakfast (plan-specific)',
  mid_morning: 'Light mid-morning snack (plan-specific)',
  lunch: 'Seasonal lunch thali (plan-specific)',
  evening_snack: 'Evening snack (plan-specific)',
  dinner: 'Homestyle dinner (plan-specific)',
};

export function fillAllSlotTypes(weeklyMenu = []) {
  return weeklyMenu.map((row) => {
    const existing = row.meals || [];
    const have = new Set(existing.map((m) => m.slotId || resolveSlotType(m.slot)?.id));
    const filled = [...existing];
    for (const slotType of MEAL_SLOT_TYPES) {
      if (!have.has(slotType.id)) {
        filled.push({
          slotId: slotType.id,
          slot: slotType.label,
          item: PLACEHOLDER_BY_SLOT[slotType.id],
          portion: '',
          calories: '',
        });
      }
    }
    return { ...row, meals: sortMealsBySlotOrder(filled) };
  });
}

export function expandToFullWeek(weeklyMenu = []) {
  if (!weeklyMenu.length) return [];
  let rows = weeklyMenu;
  if (weeklyMenu.length < WEEKDAY_ORDER.length) {
    rows = WEEKDAY_ORDER.map((day, i) => {
      const src = weeklyMenu[i % weeklyMenu.length];
      return { day, meals: src?.meals || [] };
    });
  }
  return fillAllSlotTypes(sortWeekdays(rows));
}

export { WEEKDAY_ORDER };
